import React, { useState, useEffect } from 'react';
import {
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { DateTime } from 'luxon';
import { exportAssessmentsToCsv } from '../../utils';
import { Assessment } from '../../routes/userprofile';
import JSZip from 'jszip';

//-------------- Types --------------

interface AssessmentGraphProps {
  backData: Assessment[];
  kneeData: Assessment[];
  armData: Assessment[];
  userEmail: string | null | undefined;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any;
}

//-------------- Custom Tooltip --------------

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div
        className="shadow-md rounded-lg border border-gray-200 bg-white"
        style={{
          padding: '10px 12px',
          fontSize: '12px',
          maxWidth: 260,
        }}
      >
        <p className="font-semibold mb-1">
          {data.startTime
            ? data.startTime.toFormat('dd/MM/yyyy HH:mm')
            : 'Unknown date'}
        </p>
        {data.platform && (
          <p className="text-xs text-gray-600 mb-2">
            Platform: <span className="font-medium">{data.platform}</span>
          </p>
        )}
        {data.flexibilityScore1 != null && (
          <p>Flexion: {data.flexibilityScore1}°</p>
        )}
        {data.flexibilityScore2 != null && (
          <p>Extension: {data.flexibilityScore2}°</p>
        )}
        {data.flexibilityScore3 != null && (
          <p>Left Rotation: {data.flexibilityScore3}°</p>
        )}
        {data.flexibilityScore4 != null && (
          <p>Right Rotation: {data.flexibilityScore4}°</p>
        )}
        {data.flexibilityScore5 != null && (
          <p>Left Lateral: {data.flexibilityScore5}°</p>
        )}
        {data.flexibilityScore6 != null && (
          <p>Right Lateral: {data.flexibilityScore6}°</p>
        )}
      </div>
    );
  }
  return null;
};

// If your line toggles differ between tabs, you can refine this
const initialLineVisibility = [true, true, true, true, true, true];

//-------------- Component --------------

const AssessmentGraph: React.FC<AssessmentGraphProps> = ({
  backData,
  kneeData,
  armData,
  userEmail,
}) => {
  const [lineVisibility, setLineVisibility] = useState<boolean[]>(initialLineVisibility);
  const [filteredData, setFilteredData] = useState<Assessment[]>([]);
  const [activeTab, setActiveTab] = useState<string>('Back');
  const [showTable, setShowTable] = useState<boolean>(false);

  // Either a number (days) or 'all' for "All Time"
  const [daysShown, setDaysShown] = useState<number | 'all'>(7);

  // The date range displayed
  const [currentStart, setCurrentStart] = useState<DateTime>(
    DateTime.now().minus({ days: 6 }).startOf('day')
  );
  const [currentEnd, setCurrentEnd] = useState<DateTime>(
    DateTime.now().endOf('day')
  );

  // The time ranges (including "All Time" and "1 Year")
  const timeRanges = [
    { label: 'Single Day', value: 1 },
    { label: '7 Days', value: 7 },
    { label: '1 Month', value: 28 },
    { label: '1 Year', value: 365 },
    { label: 'All Time', value: 'all' },
  ];

  //--- Toggle line visibility (per index) ---
  const toggleLineVisibility = (index: number) => {
    const newVisibility = [...lineVisibility];
    newVisibility[index] = !newVisibility[index];
    setLineVisibility(newVisibility);
  };

  //--- Switch tabs: Back / Knee / Arm ---
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  //--- SHIFT RANGE: Previous ---
  const handlePrevious = () => {
    if (daysShown === 'all') return; // no shifting in All Time
    const offset = daysShown === 1 ? { days: 1 } : { days: daysShown };
    setCurrentStart(currentStart.minus(offset));
    setCurrentEnd(currentEnd.minus(offset));
  };

  //--- SHIFT RANGE: Next ---
  const handleNext = () => {
    if (daysShown === 'all') return; // no shifting in All Time
    const offset = daysShown === 1 ? { days: 1 } : { days: daysShown };
    setCurrentStart(currentStart.plus(offset));
    setCurrentEnd(currentEnd.plus(offset));
  };

  //--- TIME RANGE SELECT ---
  const handleTimeRangeChange = (value: number | 'all') => {
    setDaysShown(value);

    if (value === 'all') {
      // Combine all data from current tab to find earliest & latest
      let dataToFilter = backData;
      if (activeTab === 'Knee') dataToFilter = kneeData;
      else if (activeTab === 'Arm') dataToFilter = armData;

      if (!dataToFilter || dataToFilter.length === 0) {
        // Fallback
        setCurrentStart(DateTime.now().minus({ days: 1 }));
        setCurrentEnd(DateTime.now());
        return;
      }

      const sortedByStart = [...dataToFilter].sort(
        (a, b) => (a.startTime?.toMillis() ?? 0) - (b.startTime?.toMillis() ?? 0)
      );

      const earliest = sortedByStart[0].startTime;
      const latest = sortedByStart[sortedByStart.length - 1].startTime;

      if (earliest && latest) {
        setCurrentStart(earliest.startOf('day'));
        setCurrentEnd(latest.endOf('day'));
      } else {
        setCurrentStart(DateTime.now().minus({ days: 1 }));
        setCurrentEnd(DateTime.now());
      }
    } else {
      // numeric days
      const newStart =
        value === 1
          ? DateTime.now().startOf('day')
          : DateTime.now().minus({ days: value - 1 }).startOf('day');
      const newEnd = DateTime.now().endOf('day');
      setCurrentStart(newStart);
      setCurrentEnd(newEnd);
    }
  };

  //--- Filter the relevant data by tab & date range ---
  useEffect(() => {
    let dataToFilter = backData;
    if (activeTab === 'Knee') {
      dataToFilter = kneeData;
    } else if (activeTab === 'Arm') {
      dataToFilter = armData;
    }

    const filtered = dataToFilter.filter((assessment) => {
      if (!assessment.startTime) return false;

      if (!DateTime.isDateTime(assessment.startTime)) {
        if (typeof assessment.startTime === 'string') {
          assessment.startTime = DateTime.fromISO(assessment.startTime);
        } else if (typeof assessment.startTime === 'number') {
          assessment.startTime = DateTime.fromSeconds(assessment.startTime);
        } else {
          console.warn(
            'Unrecognized format for assessment.startTime:',
            assessment.startTime
          );
        }
      }

      return (
        assessment.startTime >= currentStart &&
        assessment.startTime <= currentEnd
      );
    });
    setFilteredData(filtered);
  }, [activeTab, currentStart, currentEnd, backData, kneeData, armData, daysShown]);

  //--- Transform for Recharts ---
  const transformedData = filteredData.map((item) => ({
    ...item,
    timeStamp: item.startTime ? item.startTime.toMillis() : 0,
  }));

  //--- Generate ticks for the X-axis based on daysShown ---
  let ticks: number[] = [];
  if (daysShown === 'all') {
    const totalMs = currentEnd.toMillis() - currentStart.toMillis();
    const segmentCount = 6;
    const segmentSize = totalMs / segmentCount;
    ticks = Array.from({ length: segmentCount + 1 }).map(
      (_, i) => currentStart.toMillis() + i * segmentSize
    );
  } else if (daysShown === 1) {
    ticks = Array.from({ length: 24 }).map((_, i) =>
      currentStart.plus({ hours: i }).toMillis()
    );
  } else if (daysShown === 7) {
    ticks = Array.from({ length: 7 }).map((_, i) =>
      currentStart.plus({ days: i }).toMillis()
    );
  } else if (daysShown === 28) {
    ticks = Array.from({ length: 4 }).map((_, i) =>
      currentStart.plus({ weeks: i }).toMillis()
    );
  } else if (daysShown === 365) {
    const monthsCount = 12;
    ticks = Array.from({ length: monthsCount }).map((_, i) =>
      currentStart.plus({ months: i }).toMillis()
    );
  }

  //--- Format tick labels ---
  const formatTick = (tick: number) => {
    const dt = DateTime.fromMillis(tick);
    if (daysShown === 'all' || daysShown > 7) {
      return dt.toFormat('dd/MMM');
    } else if (daysShown === 7) {
      return dt.toFormat('EEE dd/MMM');
    } else {
      return dt.toFormat('HH:mm');
    }
  };

  //--- Disable forward arrow if at "today" or if "all" ---
  const isNextDisabled =
    daysShown === 'all' || currentEnd >= DateTime.now().endOf('day');

  //-------------- Render --------------
  return (
    <div className="card bg-white shadow-md rounded-xl p-4 h-[520px] flex flex-col">
      {/* Header row: title + tabs + export */}
        <div className="flex justify-end mb-4">
              <button
              className="btn btn-primary"
              onClick={() => downloadAssessmentsZip(backData, kneeData, armData)}
            >
              Export assessments
            </button>
            </div>
      <div className="flex items-center justify-between mb-3 gap-4">
    
        <div className="flex items-center gap-2">
          {/* Tabs */}
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            {['Back', 'Knee', 'Arm'].map((tab) => (
              <button
                key={tab}
                className={`px-3 py-1 text-xs rounded-full transition ${
                  activeTab === tab
                    ? 'bg-white shadow-sm text-gray-900 font-medium'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Export Button
          <button
            className="btn btn-xs btn-outline normal-case"
            onClick={() =>
              exportAssessmentsToCsv(
                'assessments.csv',
                backData,
                kneeData,
                armData,
                userEmail ?? ''
              )
            }
          >
            Export CSV
          </button> */}
        </div>
      </div>

      {/* Controls Row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 text-sm">
        {/* Date navigation */}
        <div className="flex items-center gap-2">
          <button
            className="btn btn-xs btn-outline"
            onClick={handlePrevious}
            title="Previous range"
          >
            ←
          </button>
          <span className="font-medium text-gray-700">
            {currentStart.toFormat('dd/MM/yyyy')} –{' '}
            {currentEnd.toFormat('dd/MM/yyyy')}
          </span>
          <button
            className="btn btn-xs btn-outline"
            onClick={handleNext}
            disabled={isNextDisabled}
            title="Next range"
          >
            →
          </button>
        </div>

        {/* Time range selector */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Range</span>
          <select
            className="select select-xs select-bordered"
            value={daysShown}
            onChange={(e) => {
              const val =
                e.target.value === 'all' ? 'all' : Number(e.target.value);
              handleTimeRangeChange(val);
            }}
          >
            {timeRanges.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>
        </div>

        {/* Table toggle */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Table view</span>
          <input
            type="checkbox"
            className="toggle toggle-xs toggle-primary"
            checked={showTable}
            onChange={() => setShowTable(!showTable)}
          />
        </div>
      </div>

      {/* --- Chart vs. Table --- */}
      {!showTable ? (
        // --- Chart View ---
        <div className="flex-1 flex flex-col">
          <div className="flex-1">
            <ResponsiveContainer
              key={`${daysShown}-${currentStart.toMillis()}-${currentEnd.toMillis()}-${activeTab}`}
            >
              <ComposedChart
                data={transformedData}
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <Tooltip content={<CustomTooltip />} />
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  interval={0}
                  ticks={ticks}
                  tickFormatter={formatTick}
                  dataKey="timeStamp"
                  type="number"
                  domain={[currentStart.toMillis(), currentEnd.toMillis()]}
                  scale="time"
                  style={{ fontSize: '11px' }}
                />
                <YAxis
                  domain={[0, 120]}
                  ticks={[30, 60, 90, 120]}
                  axisLine={{ stroke: '#AAAAAA' }}
                  tickLine={{ stroke: '#AAAAAA' }}
                  tick={{ fill: '#000000', fontSize: '11px' }}
                />
                {/* Lines */}
                {lineVisibility[0] && (
                  <Line
                    type="monotone"
                    strokeWidth={1.5}
                    dataKey="flexibilityScore1"
                    stroke="#4880B8"
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                )}
                {lineVisibility[1] && activeTab === 'Back' && (
                  <Line
                    type="monotone"
                    strokeWidth={1.5}
                    dataKey="flexibilityScore2"
                    stroke="#73E38C"
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                )}
                {lineVisibility[2] && activeTab === 'Back' && (
                  <Line
                    type="monotone"
                    strokeWidth={1.5}
                    dataKey="flexibilityScore3"
                    stroke="#9747ff"
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                )}
                {lineVisibility[3] && activeTab === 'Back' && (
                  <Line
                    type="monotone"
                    strokeWidth={1.5}
                    dataKey="flexibilityScore4"
                    stroke="#56c1ca"
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                )}
                {lineVisibility[4] && activeTab === 'Back' && (
                  <Line
                    type="monotone"
                    strokeWidth={1.5}
                    dataKey="flexibilityScore5"
                    stroke="#F60E38"
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                )}
                {lineVisibility[5] && activeTab === 'Back' && (
                  <Line
                    type="monotone"
                    strokeWidth={1.5}
                    dataKey="flexibilityScore6"
                    stroke="#FFA452"
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Legend-like buttons */}
          <div className="mt-2">
            <ul className="flex flex-wrap gap-2 text-xs">
              {activeTab === 'Back' && (
                <>
                  <li>
                    <button
                      className="btn btn-xs bg-base-100"
                      style={{
                        color: '#4880B8',
                        opacity: lineVisibility[0] ? 1 : 0.25,
                      }}
                      onClick={() => toggleLineVisibility(0)}
                    >
                      Flexion
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-xs bg-base-100"
                      style={{
                        color: '#73E38C',
                        opacity: lineVisibility[1] ? 1 : 0.25,
                      }}
                      onClick={() => toggleLineVisibility(1)}
                    >
                      Extension
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-xs bg-base-100"
                      style={{
                        color: '#9747ff',
                        opacity: lineVisibility[2] ? 1 : 0.25,
                      }}
                      onClick={() => toggleLineVisibility(2)}
                    >
                      Left Rotation
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-xs bg-base-100"
                      style={{
                        color: '#56c1ca',
                        opacity: lineVisibility[3] ? 1 : 0.25,
                      }}
                      onClick={() => toggleLineVisibility(3)}
                    >
                      Right Rotation
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-xs bg-base-100"
                      style={{
                        color: '#F60E38',
                        opacity: lineVisibility[4] ? 1 : 0.25,
                      }}
                      onClick={() => toggleLineVisibility(4)}
                    >
                      Left Lateral
                    </button>
                  </li>
                  <li>
                    <button
                      className="btn btn-xs bg-base-100"
                      style={{
                        color: '#FFA452',
                        opacity: lineVisibility[5] ? 1 : 0.25,
                      }}
                      onClick={() => toggleLineVisibility(5)}
                    >
                      Right Lateral
                    </button>
                  </li>
                </>
              )}
              {activeTab === 'Knee' && (
                <li>
                  <button
                    className="btn btn-xs bg-base-100"
                    style={{
                      color: '#4880B8',
                      opacity: lineVisibility[0] ? 1 : 0.25,
                    }}
                    onClick={() => toggleLineVisibility(0)}
                  >
                    Extension
                  </button>
                </li>
              )}
              {activeTab === 'Arm' && (
                <li>
                  <button
                    className="btn btn-xs bg-base-100"
                    style={{
                      color: '#4880B8',
                      opacity: lineVisibility[0] ? 1 : 0.25,
                    }}
                    onClick={() => toggleLineVisibility(0)}
                  >
                    Flexion
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      ) : (
        // --- TABLE VIEW ---
        <div className="flex-1 overflow-auto mt-1">
          {activeTab === 'Back' && (
            <table className="table table-zebra table-sm text-xs">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Platform</th>
                  <th>Start Time</th>
                  <th>Flexion</th>
                  <th>Extension</th>
                  <th>Left Rotation</th>
                  <th>Right Rotation</th>
                  <th>Left Lateral</th>
                  <th>Right Lateral</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.deviceId}</td>
                    <td>{row.platform || '—'}</td>
                    <td>{row.startTime?.toFormat('yyyy-MM-dd HH:mm')}</td>
                    <td>{row.flexibilityScore1}</td>
                    <td>{row.flexibilityScore2}</td>
                    <td>{row.flexibilityScore3}</td>
                    <td>{row.flexibilityScore4}</td>
                    <td>{row.flexibilityScore5}</td>
                    <td>{row.flexibilityScore6}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {activeTab === 'Knee' && (
            <table className="table table-zebra table-sm text-xs">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Platform</th>
                  <th>Start Time</th>
                  <th>Extension</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.deviceId}</td>
                    <td>{row.platform || '—'}</td>
                    <td>{row.startTime?.toFormat('yyyy-MM-dd HH:mm')}</td>
                    <td>{row.flexibilityScore1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {activeTab === 'Arm' && (
            <table className="table table-zebra table-sm text-xs">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Platform</th>
                  <th>Start Time</th>
                  <th>Flexion</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.deviceId}</td>
                    <td>{row.platform || '—'}</td>
                    <td>{row.startTime?.toFormat('yyyy-MM-dd HH:mm')}</td>
                    <td>{row.flexibilityScore1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ------------------------
// CSV Export Helpers
// ------------------------
const buildBackAssessmentsByDeviceCsv = (backAssessments: Assessment[]): string => {
  let csv = '';

  const byDevice: Record<string, Assessment[]> = {};
  backAssessments.forEach((t) => {
    const deviceId = t.deviceId ?? 'UNKNOWN_DEVICE';
    if (!byDevice[deviceId]) byDevice[deviceId] = [];
    byDevice[deviceId].push(t);
  });

  Object.entries(byDevice).forEach(([deviceId, deviceAssessments]) => {
    deviceAssessments.sort(
      (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
    );

    csv += `Device ID: ${deviceId}\n`;
    csv += [
      'Start Time (ISO)',
      'Flexibility Score 1',
      'Flexibility Score 2',
      'Flexibility Score 3',
      'Flexibility Score 4',
      'Flexibility Score 5',
      'Flexibility Score 6',
      'Platform',
    ].join(',') + '\n';

    deviceAssessments.forEach((t) => {
      csv += [
        t.startTime.toISO(),
        t.flexibilityScore1 ?? '',
        t.flexibilityScore2 ?? '',
        t.flexibilityScore3 ?? '',
        t.flexibilityScore4 ?? '',
        t.flexibilityScore5 ?? '',
        t.flexibilityScore6 ?? '',
        t.platform ?? '',
      ].join(',') + '\n';
    });

    csv += '\n';
  });

  return csv;
};

const buildBackAssessmentsTimelineCsv = (backAssessments: Assessment[]): string => {
  const sorted = [...backAssessments].sort(
    (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
  );

  let csv =
    [
      'Start Time (ISO)',
      'Device ID',
      'Flexibility Score 1',
      'Flexibility Score 2',
      'Flexibility Score 3',
      'Flexibility Score 4',
      'Flexibility Score 5',
      'Flexibility Score 6',
      'Platform',
    ].join(',') + '\n';

  sorted.forEach((t) => {
    csv += [
      t.startTime.toISO(),
      t.deviceId ?? '',
      t.flexibilityScore1 ?? '',
      t.flexibilityScore2 ?? '',
      t.flexibilityScore3 ?? '',
      t.flexibilityScore4 ?? '',
      t.flexibilityScore5 ?? '',
      t.flexibilityScore6 ?? '',
      t.platform ?? '',
    ].join(',') + '\n';
  });

  return csv;
};

const buildKneeAssessmentsByDeviceCsv = (kneeAssessments: Assessment[]): string => {
  let csv = '';

  const byDevice: Record<string, Assessment[]> = {};
  kneeAssessments.forEach((t) => {
    const deviceId = t.deviceId ?? 'UNKNOWN_DEVICE';
    if (!byDevice[deviceId]) byDevice[deviceId] = [];
    byDevice[deviceId].push(t);
  });

  Object.entries(byDevice).forEach(([deviceId, deviceAssessments]) => {
    deviceAssessments.sort(
      (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
    );

    csv += `Device ID: ${deviceId}\n`;
    csv += [
      'Start Time (ISO)',
      'Flexibility Score 1',
      'Platform',
    ].join(',') + '\n';

    deviceAssessments.forEach((t) => {
      csv += [
        t.startTime.toISO(),
        t.flexibilityScore1 ?? '',
        t.platform ?? '',
      ].join(',') + '\n';
    });

    csv += '\n';
  });

  return csv;
};

const buildKneeAssessmentsTimelineCsv = (kneeAssessments: Assessment[]): string => {
  const sorted = [...kneeAssessments].sort(
    (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
  );

  let csv =
    [
      'Start Time (ISO)',
      'Device ID',
      'Flexibility Score 1',
      'Platform',
    ].join(',') + '\n';

  sorted.forEach((t) => {
    csv += [
      t.startTime.toISO(),
      t.deviceId ?? '',
      t.flexibilityScore1 ?? '',
      t.platform ?? '',
    ].join(',') + '\n';
  });

  return csv;
};

const buildArmAssessmentsByDeviceCsv = (armAssessments: Assessment[]): string => {
  let csv = '';

  const byDevice: Record<string, Assessment[]> = {};
  armAssessments.forEach((t) => {
    const deviceId = t.deviceId ?? 'UNKNOWN_DEVICE';
    if (!byDevice[deviceId]) byDevice[deviceId] = [];
    byDevice[deviceId].push(t);
  });

  Object.entries(byDevice).forEach(([deviceId, deviceAssessments]) => {
    deviceAssessments.sort(
      (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
    );

    csv += `Device ID: ${deviceId}\n`;
    csv += [
      'Start Time (ISO)',
      'Flexibility Score 1',
      'Flexibility Score 2',
      'Platform',
    ].join(',') + '\n';

    deviceAssessments.forEach((t) => {
      csv += [
        t.startTime.toISO(),
        t.flexibilityScore1 ?? '',
        t.flexibilityScore2 ?? '',
        t.platform ?? '',
      ].join(',') + '\n';
    });

    csv += '\n';
  });

  return csv;
};

const buildArmAssessmentsTimelineCsv = (armAssessments: Assessment[]): string => {
  const sorted = [...armAssessments].sort(
    (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
  );

  let csv =
    [
      'Start Time (ISO)',
      'Device ID',
      'Flexibility Score 1',
      'Flexibility Score 2',
      'Platform',
    ].join(',') + '\n';

  sorted.forEach((t) => {
    csv += [
      t.startTime.toISO(),
      t.deviceId ?? '',
      t.flexibilityScore1 ?? '',
      t.flexibilityScore2 ?? '',
      t.platform ?? '',
    ].join(',') + '\n';
  });

  return csv;
};

export const downloadAssessmentsZip = async (backAssessments: Assessment[], kneeAssessments: Assessment[], armAssessments: Assessment[]) => {
  if (!backAssessments.length && !kneeAssessments.length && !armAssessments.length) return;

  // Normalize timestamps (shared logic) and make files
  const normalized1 = backAssessments
    .map((t) => {
      let startTime = t.startTime;
      if (!DateTime.isDateTime(startTime)) {
        if (typeof startTime === 'string') {
          startTime = DateTime.fromISO(startTime);
        } else if (typeof startTime === 'number') {
          startTime = DateTime.fromSeconds(startTime);
        }
      }
      return startTime ? { ...t, startTime } : null;
    })
    .filter(Boolean) as Assessment[];

  const zip = new JSZip();

  zip.file(
    'back_assessments_by_device.csv',
    buildBackAssessmentsByDeviceCsv(normalized1)
  );

  zip.file(
    'back_assessments_timeline.csv',
    buildBackAssessmentsTimelineCsv(normalized1)
  );

    const normalized2 = kneeAssessments
    .map((t) => {
      let startTime = t.startTime;
      if (!DateTime.isDateTime(startTime)) {
        if (typeof startTime === 'string') {
          startTime = DateTime.fromISO(startTime);
        } else if (typeof startTime === 'number') {
          startTime = DateTime.fromSeconds(startTime);
        }
      }
      return startTime ? { ...t, startTime } : null;
    })
    .filter(Boolean) as Assessment[];

  zip.file(
    'knee_assessments_by_device.csv',
    buildKneeAssessmentsByDeviceCsv(normalized2)
  );

  zip.file(
    'knee_assessments_timeline.csv',
    buildKneeAssessmentsTimelineCsv(normalized2)
  );

  const normalized3 = armAssessments
    .map((t) => {
      let startTime = t.startTime;
      if (!DateTime.isDateTime(startTime)) {
        if (typeof startTime === 'string') {
          startTime = DateTime.fromISO(startTime);
        } else if (typeof startTime === 'number') {
          startTime = DateTime.fromSeconds(startTime);
        }
      }
      return startTime ? { ...t, startTime } : null;
    })
    .filter(Boolean) as Assessment[];

  zip.file(
    'arm_assessments_by_device.csv',
    buildArmAssessmentsByDeviceCsv(normalized3)
  );

  zip.file(
    'arm_assessments_timeline.csv',
    buildArmAssessmentsTimelineCsv(normalized3)
  );

  const blob = await zip.generateAsync({ type: 'blob' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `assessments_export_${DateTime.now().toFormat(
    'yyyyLLdd_HHmm'
  )}.zip`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default AssessmentGraph;